import { useState } from 'react';
import { useAsyncData } from 'shared/hooks/useAsyncData';
import { fetchClassGroups, saveSessionAttendance, toggleClassGroupStatus } from 'modules/arena/class-groups/classGroupsService';
import { ClassGroup } from 'modules/arena/class-groups/types';

export function useClassGroups() {
  const { data: groups, isLoading, error, setData: setGroups } = useAsyncData<ClassGroup[]>(fetchClassGroups, []);
  const [selectedId, setSelectedId] = useState<string>('');
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);

  const selectedGroup = groups.find((group) => group.id === selectedId) ?? groups[0] ?? null;

  function toggleExpandSession(sessionId: string) {
    setExpandedSessionId((prev) => (prev === sessionId ? null : sessionId));
  }

  function togglePresence(sessionId: string, studentId: string) {
    setGroups((prev) =>
      prev.map((group) => ({
        ...group,
        upcomingSessions: group.upcomingSessions.map((session) =>
          session.id !== sessionId
            ? session
            : {
                ...session,
                presentStudentIds: session.presentStudentIds.includes(studentId)
                  ? session.presentStudentIds.filter((id) => id !== studentId)
                  : [...session.presentStudentIds, studentId],
              }
        ),
      }))
    );
  }

  function saveAttendance(sessionId: string) {
    setGroups((prev) =>
      prev.map((group) => ({
        ...group,
        upcomingSessions: group.upcomingSessions.map((session) =>
          session.id !== sessionId ? session : { ...session, attendanceTaken: true }
        ),
      }))
    );
    setExpandedSessionId(null);

    const session = groups.flatMap((group) => group.upcomingSessions).find((item) => item.id === sessionId);
    if (session) saveSessionAttendance(sessionId, session.presentStudentIds).catch(() => {});
  }

  function toggleGroupStatus(groupId: string) {
    const current = groups.find((group) => group.id === groupId);
    if (!current) return;
    const nextStatus = current.status === 'ativa' ? 'pausada' : 'ativa';

    setGroups((prev) => prev.map((group) => (group.id !== groupId ? group : { ...group, status: nextStatus })));
    toggleClassGroupStatus(groupId, nextStatus).catch(() => {});
  }

  return {
    groups,
    isLoading,
    error,
    selectedId,
    setSelectedId,
    selectedGroup,
    expandedSessionId,
    toggleExpandSession,
    togglePresence,
    saveAttendance,
    toggleGroupStatus,
  };
}
